/**
 * TAR (Team Alignment Report) PDF export utility.
 *
 * Mirrors the existing Roadmap PDF export pattern in RoadmapBuilderSession.tsx:
 * same CSS, same print styles, same cover page, same action bar.
 * Uses window.open() + document.write() → browser print dialog.
 */

interface TarPdfMetadata {
  /** Roadmap/session company name */
  companyName: string;
  /** e.g. "QBR — Q2 FY2026" or "Weekly — Mar 23–29, 2026" */
  periodLabel: string;
  /** Team member names who contributed */
  contributorNames?: string[];
  /** ISO date of when TAR was generated */
  generatedAt?: string;
  /** Session name (for General Studio context) */
  sessionName?: string;
}

const esc = (s: string) =>
  (s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/** Convert markdown to simple HTML — mirrors the mdToHtml in generatePdfHtml */
function mdToHtml(text: string): string {
  let h = esc(text)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/`(.+?)`/g, "<code>$1</code>")
    .replace(/^### (.+)$/gm, '<h4 class="tar-h4">$1</h4>')
    .replace(/^## (.+)$/gm, '<h3 class="tar-h3">$1</h3>')
    .replace(/^# (.+)$/gm, '<h2 class="tar-h2">$1</h2>')
    .replace(/^[-•*] (.+)$/gm, "<li>$1</li>")
    .replace(/^\d+\.\s(.+)$/gm, "<li>$1</li>")
    .replace(/^---$/gm, '<hr class="tar-hr">')
    .replace(/\n{2,}/g, "</p><p>")
    .replace(/\n/g, "<br>");
  // Wrap consecutive <li> into <ul>
  h = h.replace(/((?:<li>.*?<\/li>(?:<br>)?)+)/g, "<ul>$1</ul>");
  // Clean up <br> inside <ul>
  h = h.replace(/<ul>(.*?)<\/ul>/gs, (_, inner) =>
    `<ul>${inner.replace(/<br>/g, "")}</ul>`
  );
  return h.startsWith("<") ? h : `<p>${h}</p>`;
}

export function generateTarPdfHtml(
  tarMarkdown: string,
  metadata: TarPdfMetadata
): string {
  const now = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const generatedDate = metadata.generatedAt
    ? new Date(metadata.generatedAt).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : now;
  const contributors = metadata.contributorNames?.length
    ? metadata.contributorNames.join(", ")
    : "";

  const css = `
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
body{font-family:Georgia,'Times New Roman',serif;font-size:11pt;line-height:1.65;color:#1a1a1a;background:#fff}
.cover{display:flex;flex-direction:column;justify-content:center;min-height:100vh;padding:60pt 72pt;page-break-after:always}
.cover-logo{font-size:12pt;font-weight:700;letter-spacing:.15em;text-transform:uppercase;color:#802a88;margin-bottom:48pt;font-family:Arial,sans-serif}
.cover-title{font-size:34pt;line-height:1.2;color:#1a1a1a;margin-bottom:10pt}
.cover-sub{font-size:13pt;color:#6b7280;font-family:Arial,sans-serif;margin-bottom:6pt}
.cover-meta{font-size:9.5pt;color:#9ca3af;margin-top:48pt;padding-top:14pt;border-top:1pt solid #e5e7eb;font-family:Arial,sans-serif}
.cover-meta p{margin-bottom:3pt}
.content{max-width:170mm;margin:0 auto;padding:20pt 0 40pt}
.tar-body{font-size:10.5pt;line-height:1.7;color:#374151}
.tar-body p{margin-bottom:8pt}
.tar-body p:last-child{margin-bottom:0}
.tar-h2{font-size:16pt;color:#2c1561;margin:20pt 0 6pt;page-break-after:avoid}
.tar-h3{font-size:13pt;color:#2c1561;margin:16pt 0 4pt;page-break-after:avoid}
.tar-h4{font-size:11pt;margin:10pt 0 3pt;color:#374151;font-style:italic}
.tar-body ul{padding-left:16pt;margin:4pt 0 10pt}
.tar-body li{margin-bottom:3pt;font-size:10.5pt;line-height:1.6}
.tar-body strong{font-weight:700;color:#1a1a1a}
.tar-body em{font-style:italic}
.tar-body code{font-family:'Courier New',monospace;font-size:8.5pt;background:#f3f4f6;padding:1pt 3pt;border-radius:2pt}
.tar-hr{border:none;border-top:1pt solid #e5e7eb;margin:14pt 0}
.action-bar{position:fixed;top:0;left:0;right:0;z-index:100;background:#fff;border-bottom:1.5pt solid #e5e7eb;padding:10pt 24pt;display:flex;align-items:center;gap:12pt;font-family:Arial,sans-serif;box-shadow:0 1px 4px rgba(0,0,0,.06)}
.action-bar-logo{font-size:10pt;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#802a88}
.action-bar-title{font-size:10pt;color:#6b7280;flex:1}
.action-bar-btn{background:#2c1561;color:#fff;border:none;border-radius:6pt;padding:7pt 16pt;font-size:10pt;font-weight:600;cursor:pointer;font-family:Arial,sans-serif;letter-spacing:.02em}
.action-bar-btn:hover{background:#3d1f80}
.action-bar-hint{font-size:8.5pt;color:#9ca3af}
body{padding-top:48pt}
@media print{.action-bar{display:none!important}body{padding-top:0}.cover{min-height:100vh}}`;

  const title = `${esc(metadata.companyName)} — Team Alignment Report`;

  return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><title>${title}</title><style>${css}</style></head><body>
<div class="action-bar">
  <span class="action-bar-logo">Vojomo</span>
  <span class="action-bar-title">${esc(metadata.companyName)} · Team Alignment Report · ${esc(metadata.periodLabel)}</span>
  <button class="action-bar-btn" onclick="window.print()">Save as PDF</button>
  <span class="action-bar-hint">Cmd+P</span>
</div>
<div class="cover">
  <div class="cover-logo">Vojomo</div>
  <h1 class="cover-title">${esc(metadata.companyName)}</h1>
  <p class="cover-sub">Team Alignment Report · ${esc(metadata.periodLabel)}</p>
  <div class="cover-meta">
    <p>Generated ${esc(generatedDate)} · Vojomo Roadmap Builder</p>
    ${contributors ? `<p>Contributors: ${esc(contributors)}</p>` : ""}
    ${metadata.sessionName ? `<p>Session: ${esc(metadata.sessionName)}</p>` : ""}
  </div>
</div>
<div class="content">
  <div class="tar-body">
    ${mdToHtml(tarMarkdown)}
  </div>
</div>
</body></html>`;
}

/** Open a new window with the TAR PDF HTML, triggering browser print dialog. */
export function exportTarPdf(tarMarkdown: string, metadata: TarPdfMetadata): void {
  const html = generateTarPdfHtml(tarMarkdown, metadata);
  const win = window.open("", "_blank");
  if (!win) {
    alert("Pop-up blocked — please allow pop-ups for this site to export.");
    return;
  }
  win.document.write(html);
  win.document.close();
}
